import { Button, Icon, IconSizeClasses, Tooltip } from '@eppendorf/vnls-react-components';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { buildUserId } from '$shared/auth/build-user-id';
import { useTokenAttributes } from '$shared/custom-hooks/useTokenAttributes';
import { fetcher } from '$shared/fetch';
import { DASHBOARD_FEEDBACK_PATH } from '$shared/utils/api-paths';

import './section-feedback.scss';

interface SectionFeedbackProps {
  sectionId: string;
}

type FeedbackValue = 'up' | 'down' | null;
type StoredFeedback = Exclude<FeedbackValue, null>;

const isFeedbackValue = (value: unknown): value is StoredFeedback =>
  value === 'up' || value === 'down';

const readStore = (storageKey: string): Map<string, StoredFeedback> => {
  const store = new Map<string, StoredFeedback>();
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return store;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return store;
    Object.entries(parsed as Record<string, unknown>).forEach(([key, value]) => {
      if (isFeedbackValue(value)) {
        store.set(key, value);
      }
    });
  } catch {
    // ignore corrupted / unavailable storage
  }
  return store;
};

const writeStore = (storageKey: string, store: Map<string, StoredFeedback>): void => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(Object.fromEntries(store)));
  } catch {
    // ignore quota / unavailable storage
  }
};

function ScopedSectionFeedback({
  sectionId,
  storageKey,
}: SectionFeedbackProps & { storageKey?: string }): ReactElement {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState<FeedbackValue>(() =>
    storageKey ? (readStore(storageKey).get(sectionId) ?? null) : null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [hasError, setHasError] = useState(false);

  const controller = useRef<AbortController | undefined>(undefined);
  const pending = useRef(false);
  useEffect(() => {
    const current = new AbortController();
    controller.current = current;
    return () => current.abort();
  }, []);

  const handleFeedback = async (value: StoredFeedback) => {
    const signal = controller.current?.signal;
    if (!storageKey || !signal || signal.aborted || pending.current) return;
    pending.current = true;
    const previousValue = feedback;
    const newValue: FeedbackValue = feedback === value ? null : value;
    setFeedback(newValue);
    setHasError(false);
    setIsSaving(true);
    try {
      await fetcher.post(
        DASHBOARD_FEEDBACK_PATH,
        { sectionId, feedback: newValue },
        { signal },
      );
      if (signal.aborted) return;
      const store = readStore(storageKey);
      if (newValue) store.set(sectionId, newValue);
      else store.delete(sectionId);
      writeStore(storageKey, store);
    } catch {
      if (signal.aborted) return;
      setFeedback(previousValue);
      setHasError(true);
    } finally {
      if (!signal.aborted) {
        pending.current = false;
        setIsSaving(false);
      }
    }
  };

  return (
    <div className="section-feedback" data-section={sectionId}>
      <Tooltip
        trigger={
          <Button
            disabled={isSaving || !storageKey}
            type="button"
            size="small"
            variant="icon"
            className={`section-feedback__btn${feedback === 'up' ? ' section-feedback__btn--active' : ''}`}
            aria-label={t('dashboard.feedback.likeAria')}
            aria-pressed={feedback === 'up'}
            onClick={() => handleFeedback('up')}
          >
            <Icon name="chat-thumbs-up" size={IconSizeClasses.XSmall} />
          </Button>
        }
      >
        {t('dashboard.feedback.useful')}
      </Tooltip>
      <Tooltip
        trigger={
          <Button
            disabled={isSaving || !storageKey}
            type="button"
            size="small"
            variant="icon"
            className={`section-feedback__btn${feedback === 'down' ? ' section-feedback__btn--active' : ''}`}
            aria-label={t('dashboard.feedback.dislikeAria')}
            aria-pressed={feedback === 'down'}
            onClick={() => handleFeedback('down')}
          >
            <Icon name="chat-thumbs-down" size={IconSizeClasses.XSmall} />
          </Button>
        }
      >
        {t('dashboard.feedback.notUseful')}
      </Tooltip>
      {hasError && (
        <span className="section-feedback__error" role="alert">
          {t('dashboard.feedback.saveError')}
        </span>
      )}
    </div>
  );
}

export function SectionFeedback({ sectionId }: SectionFeedbackProps): ReactElement {
  const { email, tenantId } = useTokenAttributes(['email', 'tenantId']);
  const { data: userId } = useQuery({
    queryKey: ['dashboardFeedbackIdentity', tenantId, email],
    queryFn: () => buildUserId(email, tenantId),
    enabled: Boolean(email && tenantId),
  });
  const storageKey =
    tenantId && userId ? `vnls.dashboard.feedback.v2.${tenantId}.${userId}` : undefined;
  return (
    <ScopedSectionFeedback
      key={JSON.stringify([storageKey, sectionId])}
      sectionId={sectionId}
      storageKey={storageKey}
    />
  );
}
